import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/store/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/Home.vue'),
    meta: { title: 'AI Buzz Central - Your Hub for AI Innovation' }
  },
  {
    path: '/courses',
    name: 'Courses',
    component: () => import('@/pages/Courses.vue'),
    meta: { title: 'Courses - AI Buzz Central' }
  },
  {
    path: '/groups',
    name: 'Groups',
    component: () => import('@/pages/Groups.vue'),
    meta: { title: 'Groups - AI Buzz Central' }
  },
  {
    path: '/groups/:id',
    name: 'GroupDetail',
    component: () => import('@/pages/GroupDetail.vue'),
    meta: { title: 'Group Details - AI Buzz Central' }
  },
  {
    path: '/discussions',
    name: 'Discussions',
    component: () => import('@/pages/Discussions.vue'),
    meta: { title: 'Discussions - AI Buzz Central' }
  },
  {
    path: '/discussions/:id',
    name: 'DiscussionDetail',
    component: () => import('@/pages/DiscussionDetail.vue'),
    meta: { title: 'Discussion - AI Buzz Central' }
  },
  {
    path: '/events',
    name: 'Events',
    component: () => import('@/pages/Events.vue'),
    meta: { title: 'Events - AI Buzz Central' }
  },
  {
    path: '/events/:id',
    name: 'EventDetail',
    component: () => import('@/pages/EventDetail.vue'),
    meta: { title: 'Event Details - AI Buzz Central' }
  },
  {
    path: '/profile/:id?',
    name: 'Profile',
    component: () => import('@/pages/Profile.vue'),
    meta: {
      title: 'Profile - AI Buzz Central',
      requiresAuth: true
    }
  },
  {
    path: '/creator-dashboard',
    name: 'CreatorDashboard',
    component: () => import('@/pages/CreatorDashboard.vue'),
    meta: {
      title: 'Creator Dashboard - AI Buzz Central',
      requiresAuth: true
    }
  },
  {
    path: '/content/:id',
    name: 'ContentDetail',
    component: () => import('@/pages/ContentDetail.vue'),
    meta: { title: 'Content - AI Buzz Central' }
  },
  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: () => import('@/pages/Leaderboard.vue'),
    meta: { title: 'Leaderboard - AI Buzz Central' }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/pages/Notifications.vue'),
    meta: {
      title: 'Notifications - AI Buzz Central',
      requiresAuth: true
    }
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import('@/pages/Search.vue'),
    meta: { title: 'Search - AI Buzz Central' }
  },
  {
    path: '/reels',
    name: 'Reels',
    component: () => import('@/pages/Reels.vue'),
    meta: { title: 'Community Reels - AI Buzz Central' }
  },
  {
    path: '/social/instagram',
    name: 'InstagramConnect',
    component: () => import('@/pages/InstagramConnect.vue'),
    meta: {
      title: 'Connect Instagram - AI Buzz Central',
      requiresAuth: true
    }
  },
  {
    path: '/auth/instagram/callback',
    name: 'InstagramCallback',
    component: () => import('@/pages/InstagramConnect.vue'),
    meta: {
      title: 'Connecting Instagram - AI Buzz Central',
      requiresAuth: true
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/pages/Dashboard.vue'),
    meta: {
      title: 'Dashboard - AI Buzz Central',
      requiresAuth: true
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/Login.vue'),
    meta: {
      title: 'Login - AI Buzz Central',
      hideNavigation: true,
      hideFooter: true
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/pages/Register.vue'),
    meta: {
      title: 'Register - AI Buzz Central',
      hideNavigation: true,
      hideFooter: true
    }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/pages/AuthCallback.vue'),
    meta: {
      title: 'Completing Login - AI Buzz Central',
      hideNavigation: true,
      hideFooter: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFound.vue'),
    meta: { title: '404 - Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  // Update document title
  document.title = to.meta.title as string || 'AI Buzz Central'

  // Check authentication
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return URL
    next({
      name: 'Login',
      query: { redirect: to.fullPath }
    })
  } else if ((to.name === 'Login' || to.name === 'Register') && authStore.isAuthenticated) {
    // Redirect to home if already authenticated
    next({ name: 'Home' })
  } else {
    next()
  }
})

// Global error handler
router.onError((error) => {
  console.error('Router error:', error)
})

export default router
